// Service to fetch and manage datasets
// Fetches from backend API which dynamically reads from the datasets folder
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

/**
 * Fetches all datasets from the backend API
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchDatasets = async (addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl('/datasets'), {
      headers: getAuthHeaders(idToken)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch datasets: ${response.statusText}`;
      console.error(`Failed to fetch datasets: ${response.status} ${response.statusText}`, errorData);
      throw new Error(errorMsg);
    }
    const datasets = await response.json();
    console.log('Fetched datasets from backend:', datasets);
    return datasets;
  } catch (error) {
    // Only report error once - here in the catch block
    const errorMsg = error.message || 'Error fetching datasets';
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error('Error fetching datasets:', error);
    // Re-throw so the caller can handle/display the error
    throw error;
  }
};

/**
 * Fetch lightweight metadata for a set of datasets (segments count + duration, etc).
 * @param {string[]} ids
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array<{id:string, segmentCount:number|null, locationCount:number|null, startTimestamp:number|null, endTimestamp:number|null, durationSeconds:number|null}>>}
 */
export const fetchDatasetsMetadata = async (ids, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    if (!Array.isArray(ids) || ids.length === 0) {
      return [];
    }

    const response = await fetch(buildApiUrl('/datasets/metadata'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ ids })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch dataset metadata: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || 'Error fetching dataset metadata';
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error('Error fetching dataset metadata:', error);
    return [];
  }
};

/**
 * Rename a dataset (updates display name; does NOT change dataset id/folder name).
 * @param {string} datasetId
 * @param {string} name
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<{id:string, name:string}>}
 */
export const renameDataset = async (datasetId, name, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/datasets/${encodeURIComponent(datasetId)}/name`), {
      method: 'PUT',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ name })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to rename dataset: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error renaming dataset ${datasetId}`;
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error(`Error renaming dataset ${datasetId}:`, error);
    throw error;
  }
};

/**
 * Updates the trim start/end times for a dataset.
 * Pass null for both to clear the trim.
 * @param {string} datasetId
 * @param {number|null} startTime - epoch seconds
 * @param {number|null} endTime - epoch seconds
 * @param {Function} addError
 */
export const updateDatasetTrim = async (datasetId, startTime, endTime, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl(`/datasets/${encodeURIComponent(datasetId)}/trim`), {
      method: 'PUT',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ startTime, endTime })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to update trim: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error updating trim for ${datasetId}`;
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error(`Error updating trim for ${datasetId}:`, error);
    throw error;
  }
};

/**
 * Fetches a specific dataset by ID from the backend API
 * @param {string} datasetId - Dataset ID
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchDatasetById = async (datasetId, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/datasets/${encodeURIComponent(datasetId)}`), {
      headers: getAuthHeaders(idToken)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch dataset: ${response.statusText}`;
      throw new Error(errorMsg);
    }
    const dataset = await response.json();
    return dataset;
  } catch (error) {
    // Only report error once - here in the catch block
    const errorMsg = error.message || `Error fetching dataset ${datasetId}`;
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error(`Error fetching dataset ${datasetId}:`, error);
    throw error;
  }
};

/**
 * Fetches all location data from a dataset via the backend API
 * @param {string} datasetId - Dataset ID
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchDatasetLocations = async (datasetId, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/datasets/${encodeURIComponent(datasetId)}/locations`), {
      headers: getAuthHeaders(idToken)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch locations: ${response.statusText}`;
      throw new Error(errorMsg);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    // Only report error once - here in the catch block
    const errorMsg = error.message || `Error fetching locations for dataset ${datasetId}`;
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error(`Error fetching locations for dataset ${datasetId}:`, error);
    throw error;
  }
};

/**
 * Fetch feature segments for a dataset.
 * @param {string} datasetId
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array>}
 */
export const fetchFeatureSegments = async (datasetId, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/datasets/${encodeURIComponent(datasetId)}/feature-segments`), {
      headers: getAuthHeaders(idToken)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch feature segments: ${response.statusText}`;
      throw new Error(errorMsg);
    }
    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error fetching feature segments for dataset ${datasetId}`;
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error(`Error fetching feature segments for dataset ${datasetId}:`, error);
    throw error;
  }
};

/**
 * Create a feature segment for a dataset.
 * @param {string} datasetId
 * @param {{startTime:number, endTime:number, featureType:'run'|'lift'}} segment
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Object>}
 */
export const createFeatureSegment = async (datasetId, segment, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/datasets/${encodeURIComponent(datasetId)}/feature-segments`), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(segment)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to create feature segment: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error creating feature segment for dataset ${datasetId}`;
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error(`Error creating feature segment for dataset ${datasetId}:`, error);
    throw error;
  }
};

/**
 * Delete a feature segment by id.
 * @param {string} datasetId
 * @param {string} segmentId
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<{ok:boolean, deletedId:string}>}
 */
export const deleteFeatureSegment = async (datasetId, segmentId, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/datasets/${encodeURIComponent(datasetId)}/feature-segments/${encodeURIComponent(segmentId)}`), {
      method: 'DELETE',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to delete feature segment: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error deleting feature segment ${segmentId} for dataset ${datasetId}`;
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error(`Error deleting feature segment ${segmentId} for dataset ${datasetId}:`, error);
    throw error;
  }
};

/**
 * Delete a dataset directory (server-side).
 * @param {string} datasetId
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<{ok:boolean, deletedId:string}>}
 */
export const deleteDataset = async (datasetId, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/datasets/${encodeURIComponent(datasetId)}`), {
      method: 'DELETE',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to delete dataset: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error deleting dataset ${datasetId}`;
    if (addError) addError(errorMsg, error.stack, 'Datasets API');
    console.error(`Error deleting dataset ${datasetId}:`, error);
    throw error;
  }
};

